<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { ref } from 'vue';
import { useStore } from 'vuex';

import Checkbox from '@/components/base/form/Checkbox.vue';
import Modal from '@/components/base/layout/Modal.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import Header4 from '@/components/base/typography/Header4.vue';
import AddressForm from '@/components/checkout/AddressForm.vue';
import AddressList from '@/components/checkout/steps/address/AddressList.vue';
import { useCheckout } from '@/composables/useCheckout';
import { useAddressFields } from '@/composables/useForm';
import { useCustomer } from '@/stores/customer';
import { NutsAddress } from '@/utils/address';

type ModalState = 'add' | 'list';

const props = defineProps<{
  isOpen: boolean;
  address: NutsAddress;
  addresses: NutsAddress[];
}>();
const emit = defineEmits(['handle-close', 'update:address']);

const store = useStore();
const { cxMode } = useCheckout(store);
const { customer } = storeToRefs(useCustomer());
const { addAddress } = useCustomer();
const { initializeAddress } = useAddressFields();

const addressToAdd = ref<NutsAddress>(initializeAddress());
const selectedAddressId = ref(props.address.id);
const saveToAddressBook = ref(true);
const modalState = ref<ModalState>('list');

const resetModal = () => {
  modalState.value = 'list';
  addressToAdd.value = initializeAddress();
};

const handleClose = () => {
  resetModal();
  emit('handle-close');
};

const useSelectedAddress = () => {
  const selectedAddress = props.addresses.find((a) => a.id === selectedAddressId.value);
  emit('update:address', { key: props.address.key, ...selectedAddress });
};

const handleAddNewAddress = () => {
  modalState.value = 'add';
};

const addNewAddress = async () => {
  emit('update:address', { key: props.address.key, ...addressToAdd.value });
  if (saveToAddressBook.value) {
    const customerEmail = cxMode.value ? customer.value?.email : undefined;
    await addAddress(addressToAdd.value, customerEmail);
  }
  addressToAdd.value = initializeAddress();
};

const handleDone = () => {
  if (modalState.value === 'list') {
    useSelectedAddress();
  } else if (modalState.value === 'add') {
    addNewAddress();
  }

  handleClose();
};
</script>

<template>
  <Modal
    data-test="address-book-modal"
    :hasControls="false"
    :isOpen
    width="md:max-w-lg"
    @handle-close="handleClose"
  >
    <template #header>
      <Header4 v-if="modalState === 'list'">Select Address</Header4>
      <Header4 v-if="modalState === 'add'">Enter a new Address</Header4>
    </template>
    <template #body>
      <AddressList
        v-if="modalState === 'list'"
        class="mt-8"
        v-model="selectedAddressId"
        :addresses
        :showEditButton="false"
      />
      <AddressForm
        v-if="modalState === 'add'"
        class="mt-8"
        v-model="addressToAdd"
        uniqueIdSuffix="add-address-to-address-book"
        :includeFormControls="false"
        showMakeDefault
      >
        <template #saveToAddressBook>
          <Checkbox
            v-model="saveToAddressBook"
            id="saveToAddressBook"
            name="saveToAddressBook"
            class="mt-4 text-base font-bold"
            data-test="save-address-checkbox"
          >
            Save Address to Address Book
          </Checkbox>
        </template>
      </AddressForm>
    </template>
    <template #modal-controls>
      <div class="flex justify-end mt-8">
        <ThemedButton
          v-if="modalState === 'list'"
          class="mr-4"
          theme="white"
          @click="handleAddNewAddress"
        >
          Ship to a New Address
        </ThemedButton>
        <ThemedButton theme="gray" @click="handleDone">Done</ThemedButton>
      </div>
    </template>
  </Modal>
</template>
